/**
 * Weather you can see: clouds, rain, snow, lightning.
 *
 * - **Clouds** are low-poly puffs — faceted icosahedron clusters in the
 *   diorama style — in a ring around the player, as many as the cloud cover
 *   calls for, drifting with the real wind and wrapping round so the sky is
 *   never empty behind you.
 * - **Rain** is a few thousand streaks and **snow** a few thousand flakes in a
 *   box that follows the camera. Their motion is computed in the vertex shader
 *   from the clock, so falling precipitation costs no CPU at all; intensity
 *   sets how many are drawn.
 * - **Lightning** flashes the sky at random in a thunderstorm; the look module
 *   turns the flash into light.
 */

import type { Camera, Scene } from 'three';
import {
  AdditiveBlending,
  BufferAttribute,
  BufferGeometry,
  Group,
  IcosahedronGeometry,
  LineSegments,
  Mesh,
  MeshStandardMaterial,
  Points,
  ShaderMaterial,
  Vector3,
} from 'three';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import { makeRng } from '../core/build/rng.js';
import type { Weather } from '../core/weather/weather.js';

const CLOUDS = 46;
const CLOUD_RING = 9000;
const DROPS = 5000;
const FLAKES = 4000;
/** Side of the precipitation box around the camera, metres. */
const BOX = 60;

function cloudGeometry(rng: () => number): BufferGeometry {
  const parts: BufferGeometry[] = [];
  const blobs = 4 + Math.floor(rng() * 4);
  for (let i = 0; i < blobs; i++) {
    const r = 0.45 + rng() * 0.55;
    parts.push(new IcosahedronGeometry(r, 0)
      .scale(1, 0.62, 1)
      .translate((rng() - 0.5) * 2.4, (rng() - 0.3) * 0.5, (rng() - 0.5) * 1.2));
  }
  const g = mergeGeometries(parts.map(p => (p.index === null ? p : p.toNonIndexed())));
  parts.forEach(p => p.dispose());
  g.computeVertexNormals();
  return g;
}

const precipitationVertex = /* glsl */ `
  uniform float uTime;
  uniform vec3 uCamera;
  uniform vec3 uWind;
  uniform float uSpeed;
  uniform float uBox;
  uniform float uSway;
  attribute vec4 aSeed;   // xyz: position in the box; w: 0 top of streak, 1 bottom
  varying float vFade;
  void main() {
    vec3 p = aSeed.xyz * uBox;
    float fall = uTime * uSpeed;
    p.y -= fall;
    // Wind carries the fall sideways; snow also drifts on its own sway.
    p.xz += uWind.xz * uTime + vec2(sin(uTime * 1.3 + aSeed.x * 40.0), cos(uTime * 1.1 + aSeed.z * 40.0)) * uSway;
    // Wrap into the box centred on the camera.
    vec3 rel = mod(p - uCamera + uBox * 0.5, uBox) - uBox * 0.5;
    vec3 world = uCamera + rel;
    // A rain streak's lower end trails behind along the direction of fall.
    vec3 dir = normalize(vec3(uWind.x, -uSpeed, uWind.z));
    world += dir * aSeed.w * 0.9;
    vFade = 1.0 - smoothstep(uBox * 0.3, uBox * 0.5, length(rel));
    gl_Position = projectionMatrix * viewMatrix * vec4(world, 1.0);
    gl_PointSize = 3.0;
  }`;

const wrapRing = (v: number): number => ((((v + CLOUD_RING) % (2 * CLOUD_RING)) + 2 * CLOUD_RING) % (2 * CLOUD_RING)) - CLOUD_RING;

export class WeatherFx {
  readonly group = new Group();
  private readonly clouds: Mesh[] = [];
  private readonly cloudMaterial: MeshStandardMaterial;
  private readonly cloudOffsets: Vector3[] = [];
  private readonly rain: LineSegments;
  private readonly snow: Points;
  private readonly rainMaterial: ShaderMaterial;
  private readonly snowMaterial: ShaderMaterial;
  private time = 0;
  readonly drift = new Vector3();
  private readonly windScratch = new Vector3();
  private flashTimer = 5;
  /** 0..1 lightning flash this frame, for the look. */
  flash = 0;

  constructor(scene: Scene) {
    const rng = makeRng(0xc10d5);
    this.cloudMaterial = new MeshStandardMaterial({ color: 0xffffff, roughness: 1, flatShading: true });
    const shapes = Array.from({ length: 8 }, () => cloudGeometry(rng));
    for (let i = 0; i < CLOUDS; i++) {
      const mesh = new Mesh(shapes[i % shapes.length], this.cloudMaterial);
      const scale = 180 + rng() * 320;
      mesh.scale.set(scale, scale * (0.8 + rng() * 0.4), scale);
      mesh.rotation.y = rng() * Math.PI * 2;
      this.cloudOffsets.push(new Vector3((rng() - 0.5) * 2 * CLOUD_RING, 1200 + rng() * 700, (rng() - 0.5) * 2 * CLOUD_RING));
      mesh.frustumCulled = false;
      this.clouds.push(mesh);
      this.group.add(mesh);
    }

    const uniforms = () => ({
      uTime: { value: 0 }, uCamera: { value: new Vector3() }, uWind: { value: new Vector3() },
      uSpeed: { value: 9 }, uBox: { value: BOX }, uSway: { value: 0 }, uOpacity: { value: 0 },
    });

    const drops = new Float32Array(DROPS * 2 * 4);
    for (let i = 0; i < DROPS; i++) {
      const x = rng(), y = rng(), z = rng();
      drops.set([x, y, z, 0, x, y, z, 1], i * 8);
    }
    const rainGeometry = new BufferGeometry();
    rainGeometry.setAttribute('aSeed', new BufferAttribute(drops, 4));
    rainGeometry.setAttribute('position', new BufferAttribute(new Float32Array(DROPS * 2 * 3), 3));
    this.rainMaterial = new ShaderMaterial({
      uniforms: uniforms(),
      vertexShader: precipitationVertex,
      fragmentShader: `uniform float uOpacity; varying float vFade;
        void main() { gl_FragColor = vec4(0.72, 0.78, 0.86, uOpacity * vFade * 0.55); }`,
      transparent: true,
      depthWrite: false,
    });
    this.rain = new LineSegments(rainGeometry, this.rainMaterial);
    this.rain.frustumCulled = false;
    this.group.add(this.rain);

    const flakes = new Float32Array(FLAKES * 4);
    for (let i = 0; i < FLAKES; i++) flakes.set([rng(), rng(), rng(), 0], i * 4);
    const snowGeometry = new BufferGeometry();
    snowGeometry.setAttribute('aSeed', new BufferAttribute(flakes, 4));
    snowGeometry.setAttribute('position', new BufferAttribute(new Float32Array(FLAKES * 3), 3));
    this.snowMaterial = new ShaderMaterial({
      uniforms: { ...uniforms(), uSpeed: { value: 1.1 }, uSway: { value: 0.6 } },
      vertexShader: precipitationVertex,
      fragmentShader: `uniform float uOpacity; varying float vFade;
        void main() { float d = length(gl_PointCoord - 0.5); if (d > 0.5) discard;
          gl_FragColor = vec4(vec3(1.0), uOpacity * vFade * smoothstep(0.5, 0.15, d)); }`,
      transparent: true,
      depthWrite: false,
      blending: AdditiveBlending,
    });
    this.snow = new Points(snowGeometry, this.snowMaterial);
    this.snow.frustumCulled = false;
    this.group.add(this.snow);

    scene.add(this.group);
  }

  /**
   * Advance by `dt`. `ground` is the terrain height under the camera, so the
   * cloud base follows the land; `daylight` 0..1 dims the clouds at night.
   */
  update(dt: number, camera: Camera, weather: Weather, ground: number, daylight: number): void {
    this.time += dt;
    // Wind blows *from* windFrom; the air moves the other way.
    const toward = ((weather.windFrom + 180) * Math.PI) / 180;
    const wind = this.windScratch.set(Math.sin(toward) * weather.wind, 0, -Math.cos(toward) * weather.wind);

    // Clouds: as many as the cover asks for, drifting and wrapping.
    this.drift.addScaledVector(wind, dt * 2.5);
    const visible = Math.round(weather.cloud * CLOUDS);
    const grey = 1 - Math.min(0.55, (weather.rain / 6 + weather.snowfall / 3 + (weather.thunder ? 0.4 : 0)) * 0.5)
      - Math.max(0, weather.cloud - 0.7) * 0.5;
    this.cloudMaterial.color.setScalar(Math.max(0.16, grey * (0.24 + 0.76 * daylight)));
    const yScale = weather.cloud > 0.85 ? 0.75 : 1;
    const cx = camera.position.x, cz = camera.position.z;
    for (let i = 0; i < this.clouds.length; i++) {
      const cloud = this.clouds[i];
      const show = i < visible;
      cloud.visible = show;
      if (!show) continue;
      const o = this.cloudOffsets[i];
      cloud.position.set(
        cx + wrapRing(o.x + this.drift.x - cx),
        ground + o.y * yScale,
        cz + wrapRing(o.z + this.drift.z - cz),
      );
    }

    // Precipitation: draw a share of the particles by intensity.
    const rainShare = Math.min(1, weather.rain / 8);
    this.rain.visible = rainShare > 0.01;
    if (this.rain.visible) {
      this.rain.geometry.setDrawRange(0, Math.floor(DROPS * Math.max(0.08, rainShare)) * 2);
      this.rainMaterial.uniforms.uTime.value = this.time;
      (this.rainMaterial.uniforms.uCamera.value as Vector3).copy(camera.position);
      (this.rainMaterial.uniforms.uWind.value as Vector3).copy(wind).multiplyScalar(0.6);
      this.rainMaterial.uniforms.uOpacity.value = Math.min(1, 0.4 + rainShare) * (0.65 + 0.65 * daylight);
    }

    const snowShare = Math.min(1, weather.snowfall / 2.5);
    this.snow.visible = snowShare > 0.01;
    if (this.snow.visible) {
      this.snow.geometry.setDrawRange(0, Math.floor(FLAKES * Math.max(0.08, snowShare)));
      this.snowMaterial.uniforms.uTime.value = this.time;
      (this.snowMaterial.uniforms.uCamera.value as Vector3).copy(camera.position);
      (this.snowMaterial.uniforms.uWind.value as Vector3).copy(wind).multiplyScalar(0.35);
      this.snowMaterial.uniforms.uOpacity.value = Math.min(1, 0.4 + snowShare) * (0.65 + 0.65 * daylight);
    }

    // Lightning: a flash every few seconds in a storm, decaying fast.
    this.flash = Math.max(0, this.flash - dt * 6);
    if (weather.thunder) {
      this.flashTimer -= dt;
      if (this.flashTimer <= 0) {
        this.flash = 1;
        this.flashTimer = 3 + Math.random() * 11;
      }
    }
  }
}
